import { UIElement } from '../ui-element';
import { Labels } from '../../global/labels';
import { getChildrenForSlot, setAttributes } from '../../global/ui-helpers';
import { getDebugMode } from '../../global/config';
import { UIIcon } from '../icon/ui-icon';
import { element } from '../../global/template-literals';
import { Component } from '../../decorators/component.decorator';
import {
    UIMessageType,
    UIMessageLayout,
    type UIMessageLayoutValue,
    type UIMessageTypeValue,
} from './ui-message.types';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { TranslationLabels } from '../../types/labels.type';
import { ComponentState } from '../ui-element.const';
import styles from './ui-message.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIMessage
 * @element ui-message
 * @classdesc Represents a class for <code>ui-message</code> element.
 * Render alert message with a type.
 * @property {UIMessageLayout} layout {@attr layout} Layout for message.
 * @property {UIMessageTypeValue} type {@attr type}
 * Type of the message.
 *  {@desc info}
 *  {@desc warning}
 *  {@desc success}
 *  {@desc error}
 *  {@desc urgent}
 * @property {boolean} [closable=false] {@attr closable} add close button for 'light' layout.
 * @property {boolean} [persistent=false] {@attr persistent} persistence of message.
 * @property {boolean} [multiline=false] {@attr multiline} keeps the action button(s) on separate line.
 * @property {boolean} [scrolledTo=false] {@attr scrolled-to} indicates whether the message is to be scrolled to upon rendering.
 * @property {HTMLDivElement} content Shortcut to content element of the message.
 * @property {HTMLButtonElement} closeAction Shortcut to the button to close message.
 * @slot {@name action} Action element(s) of the message.
 * @example
 * <ui-message type="info">Info message</ui-message>
 * @example
 * <ui-message type="warning">Warning message</ui-message>
 * @example
 * <ui-message type="error">Error message</ui-message>
 * @example
 * <ui-message type="info" multiline>
 *   <p>A list inside info message</p>
 *   <ul class="regular-list -turquoise">
 *     <li>At vero eos et accusamus et iusto odio dignissimo</li>
 *     <li>Sed ut perspiciatis unde omnis iste natus error sit voluptatem</li>
 *   </ul>
 *   <ui-chip label="Activate" layout="button"></ui-chip>
 *   <ui-chip label="Close" layout="button"></ui-chip>
 * </ui-message>
 */
@Component({
    tag: 'ui-message',
    styles,
})
export class UIMessage extends UIElement {
    @Attribute(String) layout!: UIMessageLayoutValue;
    @Attribute(String) type!: UIMessageTypeValue;
    @Attribute(Boolean) closable?: boolean;
    @Attribute(Boolean) persistent?: boolean;
    @Attribute(Boolean) multiline?: boolean;
    @Attribute(Boolean) scrolledTo?: boolean;
    @Child('.ui-message__content') content!: HTMLDivElement;
    @Child('.ui-message__close-action') closeAction?: HTMLButtonElement;

    private closeActionHandler!: EventListener;

    /**
     * Layouts list.
     * @type {UIMessageLayout}
     */
    static Layout = UIMessageLayout;

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-message', {
            close: 'Close',
        });
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['scrolled-to'];
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name: string): void {
        if (this.state !== ComponentState.Rendered) {
            return;
        }

        switch (name) {
            case 'scrolled-to':
                this.maybeScrollTo();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * Returns the icon glyph for the message.
     * @returns {string}
     */
    get iconGlyph(): string {
        switch (this.type) {
            case UIMessageType.Success:
                return 'redesign-alert-success';
            case UIMessageType.Info:
                return 'redesign-alert-info';
            case UIMessageType.Warning:
            case UIMessageType.LightWarning:
                return 'redesign-alert-warning';
            case UIMessageType.Error:
            case UIMessageType.LightError:
                return 'redesign-alert-error';
            case UIMessageType.Urgent:
                return 'redesign-triangle-exclamation';
            default:
                console.warn(
                    `Wrong message type provided - ${String(this.type)}`
                );
                return '';
        }
    }

    /**
     * Checks if the node is empty.
     * @returns {boolean}
     */
    isEmpty(): boolean {
        return this.content.innerHTML.trim() === '';
    }

    /**
     * Checks if the message layout is 'light'
     * @returns {boolean}
     */
    isLightLayout(): boolean {
        return this.layout === UIMessage.Layout.Light;
    }

    /**
     * Sets content to message and shows the message.
     * Kept unsanitized because of javascript: links in messages in legacy iBank
     * @param {string} message - HTML content
     */
    showMessage(message: string) {
        // eslint-disable-next-line no-restricted-syntax
        this.content.innerHTML = message;
        if (getDebugMode()) {
            console.warn(
                '%cNB! HTML passed to UIMessage.showMessage() is not sanitized. Ensure the content is pre-sanitized.',
                'color: white; background-color: orange; font-size: 13px'
            );
        }

        this.show();
    }

    /**
     * Clears the message element.
     */
    clear(): void {
        if (this.persistent) {
            return;
        }
        this.content.innerHTML = '';
        this.hide();
        this.parentElement?.removeChild(this);
    }

    /**
     * Handle closing action for message
     */
    handleCloseAction(): void {
        this.clear();
    }

    /**
     * Scroll the host element into view if scrolled-to attribute is set
     */
    maybeScrollTo(): void {
        if (this.scrolledTo) {
            requestAnimationFrame(() => {
                this.scrollIntoView({ behavior: 'smooth' });
            });
        }
    }

    /**
     * Resolves default message type based on layout.
     * @returns {UIMessageTypeValue}
     * @private
     */
    private resolveDefaultMessageType(): UIMessageTypeValue {
        return this.isLightLayout()
            ? UIMessageType.Warning
            : UIMessageType.Info;
    }

    /**
     * @inheritDoc
     */
    render(): void {
        // @TODO UIIcon property fix: GUI-3392
        const defaultIconColor = UIIcon.colors.DEFAULT;
        const whiteIconColor = UIIcon.colors.WHITE;

        setAttributes(this, {
            layout: this.layout ?? UIMessage.Layout.Default,
            type: this.type ?? this.resolveDefaultMessageType(),
        });
        const actionSlotElements = getChildrenForSlot(this, 'action');
        if (actionSlotElements.length > 0 && this.closable) {
            this.removeAttribute('closable');
        }

        const contentElements = this.detachChildNodes();
        const actionElement =
            actionSlotElements.length > 0
                ? element`
            <div class="ui-message__action-buttons-wrapper">
              ${actionSlotElements}
            </div>
          `
                : this.closable
                  ? element`
            <div class="ui-message__close-button-wrapper">
              <button
                  type="button"
                  aria-label="${UIMessage.labels.close}"
                  class="button -destructive ui-message__close-action"
              >
                <ui-icon aria-hidden="true" glyph="redesign-x-mark" color="${defaultIconColor}"></ui-icon>
              </button>
            </div>
          `
                  : '';

        this.append(
            element`
            <div class="ui-message__icon-wrapper">
              <ui-icon
                  aria-hidden="true"
                  color="${!this.isLightLayout() ? whiteIconColor : null}"
                  glyph="${this.iconGlyph}"
                  class="ui-message__icon"
              ></ui-icon>      
          </div>`,
            element`<div class="ui-message__content">${contentElements}</div>`,
            actionElement
        );

        // Due to current problems of end of 2025, the best practice ot set all roles/arias
        // for web-components explicitly in the component code itself. Only this can more
        // or less guarantee working of assistive technologies.
        // https://ui5.github.io/webcomponents/docs/advanced/accessibility/
        // https://nolanlawson.com/2022/11/28/shadow-dom-and-accessibility-the-trouble-with-aria/
        setAttributes(this, {
            'aria-live': this.getAttribute('aria-live') ?? 'polite',
            'aria-atomic': this.getAttribute('aria-atomic') ?? 'true',
            'role': this.getAttribute('role') ?? 'alert',
        });
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.closeActionHandler = this.handleCloseAction.bind(this);
        this.closeAction?.addEventListener('click', this.closeActionHandler);

        this.maybeScrollTo();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.closeAction?.removeEventListener('click', this.closeActionHandler);
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.closeAction?.addEventListener('click', this.closeActionHandler);
    }
}
